<template>
<div>
  <div v-if="projectsLoading"
    class="container-sm background-white text-center">
    <b-spinner label="Loading" class="large-spinner"></b-spinner>
  </div>
  <div v-else class="container-sm background-white">
    <template>
      <b-navbar sticky class="return-home">
        <b-navbar-nav>
          <span><font-awesome-icon icon="caret-left" size="lg" /></span>
          <router-link :to="{ name: 'home' }">Return to Community List</router-link>
        </b-navbar-nav>
      </b-navbar>
    </template>
    <b-card no-body>
      <form class="needs-validation" novalidate>
        <div class="ad-hoc-community-form">
          <div class="assessment-title">
            Add New Community
          </div>
          <div class="instructional-text">
            Please provide basic details for the new ad hoc community to assess.
            All fields are required.
            <br>
            To get latitude and longitude, ensure you are located in the community and then click
            the 'Get Current Location' button. If prompted, allow the browser to access
            your location and the coordinates will be autopopulated.
            <br>The following Latitude, Longitude formats are supported:
            <br>30.273734, -97.739818
            <br>36°06'04.1"N 115°10'24.7"W
            <br>40.689201 N, 74.044543 W
            <br>39.423800° N, 0.397766° W
            <br><strong>Note:</strong> The community boundary
            will have to be mapped from the Community Assessor Tool after the assessment has been
            uploaded.
          </div>
          <div class="card-body">
            <b-row class="form-row">
              <b-col sm="3">
                <label class="bold-label-in-card"
                  for="type-CommunityName">Community Name
                </label>
              </b-col>
              <b-col sm="9">
                <b-form-input
                  id="CommunityName"
                  class="form-control"
                  type="text"
                  :state="getCommunityNameState(communityName)"
                  required
                  v-model="communityName"
                  aria-describedby="input-live-help input-live-feedback-CommunityName"
                  >
                </b-form-input>
                <b-form-invalid-feedback id="input-live-feedback-CommunityName">
                  Community Name must be between 1 and 200 characters.
                </b-form-invalid-feedback>
              </b-col>
            </b-row>
            <b-row class="form-row">
              <b-col sm="3">
                <label class="bold-label-in-card"
                  for="type-Project">Project
                </label>
              </b-col>
              <b-col sm="9">
                <b-form-select
                  v-model="project"
                  :options="projects"
                  required
                  :state="getProjectSelectionState()"
                  class="custom-select"
                >
                </b-form-select>
                <b-form-invalid-feedback id="input-live-feedback-Project">
                  A project must be selected.
                </b-form-invalid-feedback>
              </b-col>
            </b-row>
            <div v-if="!online">
              <b-row class="form-row">
                  <b-col sm="3">
                    <label class="bold-label-in-card"
                      for="type-LatLong">Latitude/Longitude
                    </label>
                  </b-col>
                  <b-col sm="9">
                    <b-form-input
                      id="LatLong"
                      class="form-control"
                      type="text"
                      :state="getLatLongState()"
                      required
                      v-model="latLong"
                      aria-describedby="latlong-help-block input-live-feedback-latlong"
                    >
                    </b-form-input>
                    <b-form-text id="latlong-help-block">
                      You are offline.
                      Please use your device's mapping app to copy your current latitude/longitude
                      coordinates and paste them into this field.
                    </b-form-text>
                    <b-form-invalid-feedback id="input-live-feedback-latlong">
                      This field is required and must have a valid value.
                    </b-form-invalid-feedback>
                  </b-col>
                </b-row>
            </div>
            <div v-else>
              <div>
                <b-row class="form-row">
                  <b-button variant="success"
                    @click="triggerGetCurrentPosition">
                    Get Current Location
                  </b-button>
                </b-row>
              </div>
              <div>
                <b-row class="form-row">
                  <b-col sm="3">
                    <label class="bold-label-in-card"
                      for="type-Latitude">Latitude
                    </label>
                  </b-col>
                  <b-col sm="9">
                    <b-form-input
                      id="Latitude"
                      class="form-control"
                      type="text"
                      :state="getLatitudeState()"
                      required
                      v-model="latitude"
                      aria-describedby="input-live-help input-live-feedback-latitude"
                    >
                    </b-form-input>
                    <b-form-invalid-feedback id="input-live-feedback-latitude">
                      Latitude is required and must have a valid format.
                    </b-form-invalid-feedback>
                  </b-col>
                </b-row>
                <b-row class="form-row">
                  <b-col sm="3">
                    <label class="bold-label-in-card"
                      for="type-Longitude">Longitude
                    </label>
                  </b-col>
                  <b-col sm="9">
                    <b-form-input
                      id="Longitude"
                      class="form-control"
                      type="text"
                      :state="getLongitudeState()"
                      required
                      v-model="longitude"
                      aria-describedby="long-help-block input-live-feedback-longitude"
                    >
                    </b-form-input>
                    <b-form-invalid-feedback id="input-live-feedback-latitude">
                      Longitude is required and must have a valid format.
                    </b-form-invalid-feedback>
                  </b-col>
                </b-row>
              </div>
            </div>

            <b-row class="form-row">
              <b-button @click="saveAdHocAndStartAssessment" variant="primary"
                aria-label="Start Assessment"
                type="button"
                class="finish-assessment-btn">
                Start Assessment
              </b-button>
            </b-row>
          </div>
        </div>
      </form>
    </b-card>
  </div>
</div>

</template>

<script>
import { mapState } from 'vuex';
import helpers from '@/mixins/helpers';

export default {
  name: 'AdHocCommunity',
  components: {
  },
  mixins: [helpers],
  data() {
    return {
      communityName: null,
      project: null,
      latLong: null,
      latitude: null,
      longitude: null,
    };
  },
  computed: {
    ...mapState({
      communityAssessorUrl: (state) => state.config.communityAssessorUrl,
      loggedIn: (state) => state.loggedIn,
      fireDepartments: (state) => state.fireDepartments,
      currentCommunity: (state) => state.currentCommunity,
      online: (state) => state.online,
      projectsLoading: (state) => state.projectsLoading,
      projects: (state) => state.projects,
      assessmentModel: (state) => state.assessmentModel,
      nextAdHocCommunityId: (state) => state.nextAdHocCommunityId,
    }),
  },
  mounted() {
    this.$store.dispatch('fetchConfiguration').then(() => {
      this.$store.dispatch('refreshLoggedInStatus').then(() => {
        if (this.isLoggedOutAndOnline()) {
          this.$router.push({ name: 'LogIn' });
        }
        this.$store.dispatch('fetchProjects');
        this.$store.dispatch('fetchAssessmentModel');
      });
    });
  },
  methods: {
    triggerGetCurrentPosition() {
      this.getLatLon();
    },
    getLatLon() {
      navigator.geolocation.getCurrentPosition(this.setLatLong, this.displayLatLongError);
    },
    setLatLong(pos) {
      const crd = pos.coords;
      this.latitude = crd.latitude;
      this.longitude = crd.longitude;
    },
    displayLatLongError() {
      const msg = 'You have denied this app access to your location. If you would like the latitude/longitude fields'
      + ' to autopopulate with your current coordinates, please go to your browser\'s settings and set your location'
      + ' services setting to Ask or Allow. Otherwise, you can edit the latitude and longitude fields manually, '
      + 'using one of the formats described in the guidance on the form.';
      this.showOkModal('Access to Location Denied', msg);
    },
    getCommunityNameState() {
      return this.communityName
        && this.communityName.length >= 1
        && this.communityName.length <= 200;
    },
    getProjectSelectionState() {
      if (!this.project || !this.project.ProjectId) {
        return null;
      }
      return typeof this.project.ProjectId === 'number';
    },
    getDDsigned(decimalDegrees, direction) {
      let dd = decimalDegrees;
      if (direction === 'S' || direction === 's'
        || direction === 'W' || direction === 'w') {
        dd *= -1;
      } // Don't do anything for N or E
      return dd;
    },
    convertDMSToDD(degrees, minutes, seconds, direction) {
      const dd = degrees + minutes / 60 + seconds / (60 * 60);
      return this.getDDsigned(dd, direction);
    },
    getLatLongState() {
      let latLongState = false;

      if (!this.latLong) {
        return null;
      }

      // We allow these formats for a lat/long string:
      // iOS format
      // 12.12345° N 12.122345° W

      // Google Maps formats
      // 1)
      // 39°24'58.8"N 0°24'47.0"W
      // 23°57'00.9"S 45°45'11.2"E

      // 2)
      // -23.950255, 45.753108

      // Number of decimals does not matter in any case
      // + or - signs allowed in 2)

      const iOSLatLonFormatRegex = /^(\d{1,3}\.*\d*)°\s([NS])[,]*\s(\d{1,3}\.*\d*)°\s([EW])/i;
      // 55.12345° N 12.12345° W

      const latLonRegexNoLetters = /^((-?|\+?)?\d+(\.\d+)?),*\s*((-?|\+?)?\d+(\.\d+)?)$/i;
      // matches the below. Group 1 and group 4 are the lat and lon values:
      // 9.9824245,76.5703886
      // 9.9824245, 76.5703886
      // 9.9824245 76.5703886
      // also matches with +/- signs in front of lat/lon, and with or without decimals

      const latLonRegexDMS = /^(\d{1,3})°(\d{1,3})'(\d{1,3}\.*\d*)"([NnSs]),*\s*(\d{1,3})°(\d{1,3})'(\d{1,3}\.*\d*)"([EeWw])/i;
      // matches these
      // 39°33'44.7"N, 0°39'30.1"W
      // 39°33'44.7"N 0°39'30.1"W
      // 39°33'44.7"N0°39'30.1"W

      let parts = null;

      if (iOSLatLonFormatRegex.test(this.latLong)) {
        parts = (this.latLong).match(iOSLatLonFormatRegex);
        if (parts) {
          /* eslint-disable prefer-destructuring */
          this.latitude = this.getDDsigned(parts[1], parts[2]);
          this.longitude = this.getDDsigned(parts[3], parts[4]);
          /* eslint-enable prefer-destructuring */
        }
      } else if (latLonRegexNoLetters.test(this.latLong)) {
        parts = (this.latLong).match(latLonRegexNoLetters);
        if (parts) {
          /* eslint-disable prefer-destructuring */
          this.latitude = parts[1];
          this.longitude = parts[4];
          /* eslint-enable prefer-destructuring */
        }
      } else if (latLonRegexDMS.test(this.latLong)) {
        parts = (this.latLong).match(latLonRegexDMS);
        if (parts) {
          this.latitude = this.convertDMSToDD(
            parseFloat(parts[1]),
            parseFloat(parts[2]),
            parseFloat(parts[3]),
            parts[4],
          );
          this.longitude = this.convertDMSToDD(
            parseFloat(parts[5]),
            parseFloat(parts[6]),
            parseFloat(parts[7]),
            parts[8],
          );
        }
      }

      if (this.latitude && this.longitude
        && this.latitude >= -90 && this.latitude <= 90
        && this.longitude >= -180 && this.longitude <= 180) {
        latLongState = true;
      }

      return latLongState;
    },
    getLatitudeState() {
      if (!this.latitude) {
        return null;
      }
      let isLatitudeValid = false;
      let parts = null;

      const decimalDegreesRegex = /^(-?|\+?)?(\d{1,3}\.*\d*)$/i;
      const iOSLatFormatRegex = /^(\d{1,3}\.*\d*)°\s([NS])/i;
      const latRegexNoLetters = /^((-?|\+?)?\d+(\.\d+)?)$/i;
      const latRegexDMS = /^(\d{1,3})°(\d{1,3})'(\d{1,3}\.*\d*)"([NnSs])/i;

      if (decimalDegreesRegex.test(this.latitude)) {
        this.latitude = parseFloat(this.latitude);
        isLatitudeValid = true;
      } else if (!isLatitudeValid && iOSLatFormatRegex.test(this.latitude)) {
        parts = (this.latitude).match(iOSLatFormatRegex);
        if (parts) {
          this.latitude = this.getDDsigned(parts[1], parts[2]);
          isLatitudeValid = true;
        }
      } else if (!isLatitudeValid && latRegexNoLetters.test(this.latitude)) {
        parts = (this.latitude).match(latRegexNoLetters);
        if (parts) {
          /* eslint-disable prefer-destructuring */
          this.latitude = parts[1];
          isLatitudeValid = true;
          /* eslint-enable prefer-destructuring */
        }
      } else if (!isLatitudeValid && latRegexDMS.test(this.latitude)) {
        parts = (this.latitude).match(latRegexDMS);
        if (parts) {
          this.latitude = this.convertDMSToDD(
            parseFloat(parts[1]),
            parseFloat(parts[2]),
            parseFloat(parts[3]),
            parts[4],
          );
          isLatitudeValid = true;
        }
      }

      return isLatitudeValid && this.latitude >= -90 && this.latitude <= 90;
    },
    getLongitudeState() {
      if (!this.longitude) {
        return null;
      }

      let isLongitudeValid = false;
      let parts = null;

      const decimalDegreesRegex = /^(-?|\+?)?(\d{1,3}\.*\d*)$/i;
      const iOSLonFormatRegex = /^(\d{1,3}\.*\d*)°\s([EW])/i;
      const lonRegexNoLetters = /^((-?|\+?)?\d+(\.\d+)?)$/i;
      const lonRegexDMS = /^(\d{1,3})°(\d{1,3})'(\d{1,3}\.*\d*)"([EeWw])/i;

      if (decimalDegreesRegex.test(this.longitude)) {
        this.longitude = parseFloat(this.longitude);
        isLongitudeValid = true;
      } else if (!isLongitudeValid && iOSLonFormatRegex.test(this.longitude)) {
        parts = (this.longitude).match(iOSLonFormatRegex);
        if (parts) {
          this.longitude = this.getDDsigned(parts[1], parts[2]);
          isLongitudeValid = true;
        }
      } else if (!isLongitudeValid && lonRegexNoLetters.test(this.longitude)) {
        parts = (this.longitude).match(lonRegexNoLetters);
        if (parts) {
          /* eslint-disable prefer-destructuring */
          this.longitude = parts[1];
          isLongitudeValid = true;
          /* eslint-enable prefer-destructuring */
        }
      } else if (!isLongitudeValid && lonRegexDMS.test(this.longitude)) {
        parts = (this.longitude).match(lonRegexDMS);
        if (parts) {
          this.longitude = this.convertDMSToDD(
            parseFloat(parts[1]),
            parseFloat(parts[2]),
            parseFloat(parts[3]),
            parts[4],
          );
          isLongitudeValid = true;
        }
      }

      return isLongitudeValid && this.longitude >= -180 && this.longitude <= 180;
    },
    saveAdHocAndStartAssessment(submitEvent) {
      if (!(this.getCommunityNameState() && this.getProjectSelectionState()
        && this.getLatitudeState() && this.getLongitudeState())) {
        submitEvent.preventDefault();
        submitEvent.stopPropagation();
        const forms = document.getElementsByClassName('needs-validation');
        forms[0].classList.add('was-validated');
      } else {
        // Save Ad Hoc community to the state and go to its assessment page
        const adHocAssessment = JSON.parse(JSON.stringify(this.assessmentModel));

        const currentProject = this.project;
        // fill out empty model with form details
        Object.keys(currentProject).forEach((keyName) => {
          if (Object.prototype.hasOwnProperty.call(adHocAssessment, keyName)) {
            adHocAssessment[keyName] = currentProject[keyName];
          }
        });
        adHocAssessment.CommunityName = this.communityName;
        adHocAssessment.CommunityShapeWkt = `POINT (${this.longitude} ${this.latitude})`;
        adHocAssessment.LongLat = `POINT (${this.longitude} ${this.latitude})`;
        adHocAssessment.status = 'started';
        adHocAssessment.AssessmentDate = new Date();

        // Set current Ad Hoc Community Id
        adHocAssessment.CommunityId = this.nextAdHocCommunityId;

        // Set next available Ad Hoc CommunityId. NOTE: ad hoc communities need to have negative ids
        this.$store.dispatch('setNextAdHocCommunityId');
        // call store to store the AdHocCommunity
        this.$store.dispatch('addAdHocToCommunities', adHocAssessment);

        // go to assessments page
        this.$router.push({ name: 'assessment', params: { community_id: adHocAssessment.CommunityId } });
      }
    },
  },
};
</script>
